<template>
	<Sidebar v-if="hasPermission('np.backoffice.users.addperkmembercard')" v-model:visible="createNewSidebarVisible" class="p-sidebar-lg modern-sidebar" position="right" :showCloseIcon="true">
		<Loading v-if="creating" :title="'Creating ' + entityName + '...'" :full="false" class="mt-5" />
		<div v-else class="sidebar-content">
			<div class="flex align-items-center gap-3 mb-4">
				<div class="bg-blue-100 p-2 border-round-lg">
					<i class="fa fa-credit-card text-blue-600"></i>
				</div>
				<h3 class="m-0 font-semibold text-gray-800">Create {{ entityName }}</h3>
			</div>
			<Form :fields="memberCardFields" :context="memberCardContext" @onChange="handleFormChange" @onAction="createEntity" :actionLabel="'Create'" :actionIcon="'pi pi-cloud-upload'" />
		</div>
	</Sidebar>

	<div class="page-container">
		<div class="max-w-7xl mx-auto p-6">
			<!-- Header Section -->
			<div class="bg-white border-round-2xl shadow-1 p-6 mb-6">
				<div class="flex align-items-center justify-content-between">
					<div class="flex align-items-center gap-4">
						<div class="header-icon-container">
							<i class="fa fa-credit-card text-white text-2xl"></i>
						</div>
						<div>
							<h1 class="font-bold text-gray-800 text-3xl m-0 mb-2">{{ pageTitle }}</h1>
							<p class="text-gray-600 m-0 text-lg">{{ pageDescription }}</p>
						</div>
					</div>
					<Button 
						v-if="hasPermission('np.backoffice.users.addperkmembercard')" 
						@click="createNewSidebarVisible = true" 
						:label="'New ' + entityName" 
						icon="pi pi-plus-circle"
						class="add-button" 
					/>
				</div>
			</div>

			<Loading v-if="loading" :full="false" :title="'Loading ' + pageTitle + '...'" />
			
			<Card v-else class="bg-white border-round-2xl shadow-1">
				<template #header>
					<div class="flex align-items-center gap-3 px-4 pt-3">
						<div class="bg-indigo-100 p-2 border-round-lg">
							<i class="fa fa-id-card text-indigo-600"></i>
						</div>
						<h4 class="m-0 font-semibold text-gray-800">Card List</h4>
					</div>
				</template>
				<template #content>
					<DataTable :value="entities" dataKey="id" class="p-datatable-lg modern-table" :rowHover="true" responsiveLayout="scroll">
						<template #empty>
							<div class="text-center py-5">
								<i class="fa fa-credit-card text-gray-300 text-5xl mb-3"></i>
								<p class="text-gray-500 m-0">No {{ pageTitle }} found.</p>
							</div>
						</template>
						<template #loading> Loading {{ pageTitle }}. Please wait. </template>
						<Column field="cachedMemberCard.title" header="Card Type">
							<template #body="{ data }">
								<span class="font-semibold text-gray-800">{{ data.cachedMemberCard?.title }}</span>
							</template>
						</Column>
						<Column header="Status">
							<template #body="{ data }">
								<Chip v-if="getMemberCardStatus(data) == 'Deleted'" label="Deleted" class="status-chip status-deleted" />
								<Chip v-else-if="getMemberCardStatus(data) == 'Active'" label="Active" class="status-chip status-active" />
								<Chip v-else-if="getMemberCardStatus(data) == 'NoConsent'" label="Awaiting consent" class="status-chip status-waiting" />
								<Chip v-else-if="getMemberCardStatus(data) == 'Expired'" label="Expired" class="status-chip status-expired" />
								<Chip v-else label="Inactive" class="status-chip status-inactive" />
							</template>
						</Column>
						<Column field="id" header="Card Number">
							<template #body="{ data }">
								<code class="card-number">{{ data.id }}</code>
							</template>
						</Column>
						<Column field="cachedMemberCard.onlinePosGroup" header="Online POS Group">
							<template #body="{ data }">
								<span class="text-gray-600">{{ data.cachedMemberCard?.onlinePosGroup || '—' }}</span>
							</template>
						</Column>
						<Column field="createdByName" header="Created by">
							<template #body="{ data }">
								<div class="flex align-items-center gap-2">
									<div v-if="data.createdByType === 'System'" class="creator-icon bg-blue-100">
										<i class="pi pi-cog text-blue-600" title="System"></i>
									</div>
									<div v-else-if="data.createdByType === 'Admin'" class="creator-icon bg-purple-100">
										<i class="pi pi-shield text-purple-600" title="Admin"></i>
									</div>
									<div v-else class="creator-icon bg-green-100">
										<i class="pi pi-user text-green-600" title="User"></i>
									</div>
									<span class="text-gray-700">{{ data.createdByName }}</span>
								</div>
							</template>
						</Column>
						<Column field="approvedBy" header="Approved by">
							<template #body="{ data }">
								<span class="text-gray-600">{{ data.approvedBy || '—' }}</span>
							</template>
						</Column>
						<Column field="expiresAt" header="Expires">
							<template #body="{ data }">
								<span class="text-gray-600">{{ formatDate(data.expiresAt) || '—' }}</span>
							</template>
						</Column>
						<Column headerStyle="width: 4rem; text-align: center" bodyStyle="text-align: center; overflow: visible">
							<template #body="{ data }">
								<Button 
									v-if="data.deleted == false && hasPermission('np.backoffice.users.addperkmembercard')" 
									@click="confirmDeleteEntity(data)" 
									type="button" 
									icon="pi pi-trash"
									class="p-button-rounded p-button-text p-button-danger delete-btn"
									v-tooltip.top="'Delete card'"
								></Button>
							</template>
						</Column>
					</DataTable>
				</template>
			</Card>
		</div>
	</div>
</template>

<script>
import { ref, onMounted, inject } from 'vue';

import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';

export default {
	inject: ['hasPermission'],
	components: {},
	setup() {
		const storeActionGetEntities = 'userMemberCards/getUserMemberCardListItems';
		const storeGetterGetEntities = 'userMemberCards/userMemberCardListItems';
		const storeActionCreateEntity = 'userMemberCards/add';
		const storeActionDeleteEntity = 'userMemberCards/delete';
		const entityName = 'Member Card';
		const pageTitle = 'Member Cards';
		const pageDescription = "Here you can manage the User's Member Cards";

		const memberCardFields = ref([]);

		const loading = ref(true);
		const creating = ref(false);

		const useFormatDate = inject('useFormatDate');

		const route = useRoute();
		const store = useStore();
		const toast = useToast();
		const confirm = useConfirm();

		const entities = ref([]);

		const userId = route.params.userId;
		const user = ref();
		const userMemberCards = ref([]);

		const memberCardContext = ref({
			memberCardId: { id: null },
      defaultExpirationMonths: null,
      defaultExpirationUnit: "Months",
      expiresAt: "",
			approvedBy: ""
		});

		const memberCards = ref([]);

		const createNewSidebarVisible = ref(false);

		function confirmDeleteEntity(data) {
			confirm.require({
				message: 'Are you sure you want to delete the ' + entityName + '?',
				header: 'Please confirm',
				icon: 'pi pi-exclamation-triangle',
				accept: async () => {
					loading.value = true;

					try {
						await store.dispatch(storeActionDeleteEntity, data.id);
						await reloadEntities();

						toast.add({ severity: 'success', summary: 'Success', detail: entityName + ' deleted', life: 3000 });
					} catch (err) {
						toast.add({ severity: 'error', summary: 'Error', detail: 'Could not delete ' + entityName + ': ' + err, life: 3000 });
					}

					loading.value = false;
				},
				reject: () => {},
			});
		}

    function handleFormChange(context){
      // Update default expiration months when membercard selection changes
      if (context.memberCardId?.id !== memberCardContext.value.memberCardId?.id) {
        const selectedCard = memberCards.value.find(card => card.id === context.memberCardId?.id);
        context.defaultExpirationMonths = selectedCard?.defaultExpirationMonths ?? null;
        context.defaultExpirationUnit = selectedCard?.defaultExpirationUnit ?? "Months";
      }

      memberCardContext.value = { ...context };
    }

		function getSelectedMemberCardDiscountPercentage(context) {
			if (!context?.memberCardId?.id) return 0;
			const selectedCard = memberCards.value.find(card => card.id === context.memberCardId.id);
			return selectedCard?.discountPercentage || 0;
		}

		function requiresApproval(context) {
			return getSelectedMemberCardDiscountPercentage(context) >= 50;
		}

		function validateApprovedBy(value) {
			// Only alpha characters (including Nordic: æøåÆØÅ) and spaces, min 10 chars
			const alphaOnlyRegex = /^[a-zA-ZæøåÆØÅ ]+$/;
			if (!value || value.trim().length === 0) return false;
			if (value.length < 10) return false;
			return alphaOnlyRegex.test(value);
		}

		async function createEntity(context) {
			// Validate approvedBy if required (discount >= 50%)
			if (requiresApproval(context)) {
				if (!validateApprovedBy(context.approvedBy)) {
					toast.add({ 
						severity: 'error', 
						summary: 'Validation Error', 
						detail: 'Approved by is required for cards with 50%+ discount. Must be at least 10 characters (letters only).', 
						life: 5000 
					});
					return;
				}
			}

			creating.value = true;

			console.log('CREATE MEMBER CARD:', context);
			try {
				// Calculate expiration date: use custom date if provided, otherwise calculate from default value + unit
				let expiresAt = context.expiresAt;
				if (!expiresAt && context.defaultExpirationMonths && context.defaultExpirationMonths > 0) {
					const date = new Date();
					const unit = context.defaultExpirationUnit ?? "Months";
					if (unit === "Days") {
						date.setDate(date.getDate() + context.defaultExpirationMonths);
					} else if (unit === "Weeks") {
						date.setDate(date.getDate() + context.defaultExpirationMonths * 7);
					} else {
						date.setMonth(date.getMonth() + context.defaultExpirationMonths);
					}
					expiresAt = date;
				}

				const payload = { 
					userId: userId, 
					memberCardId: context.memberCardId, 
					expiresAt: expiresAt 
				};
				
				// Only include approvedBy if required
				if (requiresApproval(context)) {
					payload.approvedBy = context.approvedBy;
				}

				await store.dispatch(storeActionCreateEntity, payload);

				toast.add({ severity: 'success', summary: 'Success', detail: entityName + ' created', life: 3000 });
				createNewSidebarVisible.value = false;

				// Reset form context
				memberCardContext.value = {
					memberCardId: { id: null },
					defaultExpirationMonths: null,
					defaultExpirationUnit: "Months",
					expiresAt: "",
					approvedBy: ""
				};

				loading.value = true;
				await reloadEntities();
			} catch (err) {
				toast.add({ severity: 'error', summary: 'Error', detail: 'Could not create ' + entityName + ': ' + err, life: 3000 });
			}

			loading.value = false;
			creating.value = false;
		}

		async function reloadEntities() {
			try {
				await store.dispatch(storeActionGetEntities, { userId: userId });
				const loadedEntities = store.getters[storeGetterGetEntities](userId);
				entities.value = JSON.parse(JSON.stringify(loadedEntities));

				console.log('MEMBER CARDS:', entities.value);
			} catch (err) {
				toast.add({ severity: 'error', summary: 'Error', detail: 'Could not load ' + pageTitle + ': ' + err, life: 3000 });
			}
		}


		async function loadRequiredData() {
			loading.value = true;

			// Load User
			await store.dispatch('users/ensureUser', { userId: userId });
			const loadedUser = store.getters['users/user'](userId);
			user.value = JSON.parse(JSON.stringify(loadedUser));

			// Load User's Member Cards
			await store.dispatch('userMemberCards/ensureUserMemberCardListItems', { userId: userId });
			const loadedUserMemberCards = store.getters['userMemberCards/userMemberCardListItems'](userId);
			userMemberCards.value = JSON.parse(JSON.stringify(loadedUserMemberCards));

			// Load Member Cards
			try {
				await store.dispatch('memberCards/ensureMemberCardListItems');
			} catch (err) {
				toast.add({ severity: 'error', summary: 'Error', detail: 'Could not load Member Cards: ' + err, life: 3000 });
			}
			const loadedMemberCards = store.getters['memberCards/memberCardListItems'];
			memberCards.value = loadedMemberCards;

			reloadEntities();

			memberCardFields.value = [
				{
					id: 'memberCardId',
					path: 'memberCardId.id',
					type: 'dropdown',
					title: 'Member Card',
					filter: 'true',
					config: {
						options: memberCards,
						optionLabel: 'title',
						optionValue: 'id',
						placeholder: 'Please select Member Card',
					},
				},
				{
					id: 'defaultExpirationMonths',
					type: 'DefaultExpirationDisplay',
					title: 'Default expiration',
				},
				{
					id: 'expiresAt',
					type: 'calendar',
					title: 'Custom expiration',
					help: 'If you wish to set another expiration date, please use this field before creating',
					config: {
						minDate: new Date(new Date().setDate(new Date().getDate() + 1)),
					},
				},
				{
					id: 'approvedBy',
					type: 'AlphaOnlyInput',
					title: 'Approved by (required for 50%+ discount)',
					help: 'Minimum 10 characters, letters only',
					condition: requiresApproval,
				},
			];

			loading.value = false;
		}

    function getMemberCardStatus(memberCard){
        if(memberCard.expired)
          return "Expired";

        if(memberCard.deleted)
          return "Deleted";

        if(!memberCard.enabled)
          return "Inactive";

        if(memberCard.needsConsent && !memberCard.consentGiven)
          return "NoConsent"

        if(memberCard.needsMarketingConsent && !memberCard.marketingConsentGiven)
          return "NoConsent"

        return "Active"
    }



		onMounted(async () => {
			await loadRequiredData();
		});

		return {
			entityName,
			pageTitle,
			pageDescription,
			loading,
			creating,

			memberCardFields,
			memberCardContext,
			createEntity,
			confirmDeleteEntity,
      getMemberCardStatus,
      handleFormChange,
			
      memberCards,

			entities,

			createNewSidebarVisible,

			formatDate: useFormatDate().formatDate,
		};
	},
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* Page container */
.page-container {
	min-height: 100vh;
	background-attachment: fixed;
}

/* Header icon container */
.header-icon-container {
	background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
	padding: 0.75rem;
	border-radius: 0.75rem;
	display: flex;
	align-items: center;
	justify-content: center;
}

/* Add button styling */
.add-button {
	background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%) !important;
	border: none !important;
	padding: 0.75rem 1.5rem !important;
	border-radius: 0.75rem !important;
	font-weight: 600 !important;
	color: white !important;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
	transition: all 0.3s ease !important;
}

.add-button:hover {
	box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
	transform: translateY(-1px) !important;
}

/* Shadow styling */
.shadow-1 {
	box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

/* Card hover effects */
:deep(.p-card) {
	transition: all 0.3s ease;
}

:deep(.p-card:hover) {
	box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.12) !important;
	transform: translateY(-2px);
}

/* Modern table styling */
.modern-table :deep(.p-datatable-thead > tr > th) {
	background: #f8fafc;
	color: #64748b;
	font-weight: 600;
	text-transform: uppercase;
	font-size: 0.75rem;
	letter-spacing: 0.05em;
	padding: 1rem;
	border: none;
	border-bottom: 2px solid #e2e8f0;
}

.modern-table :deep(.p-datatable-tbody > tr) {
	transition: all 0.2s ease;
}

.modern-table :deep(.p-datatable-tbody > tr:hover) {
	background: #f8fafc !important;
}

.modern-table :deep(.p-datatable-tbody > tr > td) {
	padding: 1rem;
	border: none;
	border-bottom: 1px solid #f1f5f9;
}

/* Card number styling */
.card-number {
	background: #f1f5f9;
	padding: 0.25rem 0.5rem;
	border-radius: 0.375rem;
	font-size: 0.875rem;
	color: #475569;
	font-family: 'Monaco', 'Menlo', monospace;
}

/* Status chip styling */
.status-chip {
	font-weight: 600;
	font-size: 0.75rem;
	padding: 0.25rem 0.75rem;
	border-radius: 9999px;
}

.status-active {
	background: #dcfce7 !important;
	color: #166534 !important;
}

.status-deleted {
	background: #fee2e2 !important;
	color: #991b1b !important;
}

.status-expired {
	background: #fef3c7 !important;
	color: #92400e !important;
}

.status-waiting {
	background: #fef3c7 !important;
	color: #92400e !important;
	white-space: nowrap;
}

.status-inactive {
	background: #f1f5f9 !important;
	color: #64748b !important;
}

/* Creator icon styling */
.creator-icon {
	width: 2rem;
	height: 2rem;
	border-radius: 0.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
}

.creator-icon i {
	font-size: 0.875rem;
}

/* Delete button styling */
.delete-btn {
	transition: all 0.2s ease !important;
}

.delete-btn:hover {
	background: #fee2e2 !important;
	transform: scale(1.1);
}

/* Modern sidebar styling */
.modern-sidebar :deep(.p-sidebar-header) {
	padding: 1.5rem;
	border-bottom: 1px solid #e5e7eb;
}

.modern-sidebar :deep(.p-sidebar-content) {
	padding: 1.5rem;
}

.sidebar-content {
	padding: 0;
}

/* Responsive improvements */
@media (max-width: 768px) {
	.max-w-7xl {
		max-width: 100%;
	}

	.p-6 {
		padding: 1rem;
	}

	:deep(.p-card-body) {
		padding: 1rem !important;
	}

	.header-icon-container {
		padding: 0.5rem;
	}

	.header-icon-container i {
		font-size: 1.25rem;
	}
}
</style>

<!--
<template>
  <div class="page">
    <overlay
      animate="zoom-in"
      :opened="addMemberCardOpened"
      :visible="addMemberCardVisible"
      :closeable="true"
      @closed="addMemberCardOpened = addMemberCardVisible = false"
    >
      <div>
        <h5>Add Member Card</h5>
        <hr/>
        
        <div class="form-group">
          <label for="memberCard">Member Card</label>
          <select v-model="selectedMemberCardId" class="form-control">
            <option disabled value="">Please select Member Card</option>
            <option v-for="(option, index) in memberCards" :key="index" :value="option.id">{{ option.title }}</option>
          </select>
        </div>

        <button @click="confirmAddMemberCard" type="button" class="mt-4 btn btn-secondary">
          <i class="fa fa-plus-circle"></i> Add Member Card
        </button>
      </div>
    </overlay>

    <div class="mb-4" style="display: flex;justify-content: space-between;align-items: end;">
      <div>
        <div>
          <p v-if="user" style="margin-bottom:0.2rem;text-transform: uppercase;">Member Cards - {{user.firstName}} {{user.lastName}}</p>
          <h2>Member Cards</h2>
        </div>
        <p>
          User's Member Cards.
        </p>
      </div>
      <div>
        <button @click="addMemberCard" type="button" class="btn btn-primary">
          <i class="fa fa-plus-circle"></i> Add Member Card
        </button>
      </div>
    </div>

    <div class="page-container">
      <Loading v-if="loading" />

      <div v-else>
        <div v-if="userMemberCards.length === 0" style="text-align: center;">
          <img src="@/assets/icons/empty-box.png" alt="" style="opacity: 0.3; width: 178px; height: 178px;">
          <h3 style="color: #b3cbe6;">There are no results.</h3>
        </div>

        <div v-else class="vs-table">
          <table>
            <thead class="vs-table__thead">
              <tr class="vs-table__tr">
                <th class="vs-table__th">
                  <div class="vs-table__th__content">
                    Card Type
                  </div>
                </th>
                <th class="vs-table__th">
                  <div class="vs-table__th__content">
                    Active
                  </div>
                </th>
                <th class="vs-table__th">
                  <div class="vs-table__th__content">
                    Card Number
                  </div>
                </th>
                <th class="vs-table__th">
                  <div class="vs-table__th__content">
                    Online POS Group
                  </div>
                </th>
                <th class="vs-table__th">
                  <div class="vs-table__th__content">
                    Actions
                  </div>
                </th>
              </tr>
            </thead>
            <tbody class="vs-table__tbody">
              <tr @click="onRowClicked(userMemberCard)" class="vs-table__tr" v-for="userMemberCard in userMemberCards" :key="userMemberCard.id">
                <td class="vs-table__td">{{userMemberCard.cachedMemberCard.title}}</td>
                <td class="vs-table__td">
                  <span class="badge bg-success" style="padding: 5px 8px;">Active</span>
                </td>
                <td class="vs-table__td">{{userMemberCard.id}}</td>
                <td class="vs-table__td">{{userMemberCard.cachedMemberCard.onlinePosGroup}}</td>
                <td class="vs-table__td">
                  <button @click.stop.prevent="deleteUserMemberCard(userMemberCard)" class="ml-2 btn btn-warning round">
                    <i class="fas fa-trash" style="font-size:0.9rem;"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'

import Overlay from "@/components/Overlay";

export default {
  components: {
    Overlay
  },
  props: {},
  setup() {
    const loading = ref(true)
    const user = ref(undefined)
    const memberCards = ref([])
    const userMemberCards = ref([])

    const addMemberCardOpened = ref(false)
    const addMemberCardVisible = ref(false)
    const selectedMemberCardId = ref()

    const store = useStore()
    const route = useRoute()
    const toast = useToast()

    const userId = route.params.userId

    function addMemberCard() {
      addMemberCardOpened.value = true
      addMemberCardVisible.value = true
    }

    async function confirmAddMemberCard() {
      loading.value = true

      addMemberCardOpened.value = false
      addMemberCardVisible.value = false

      try {
        await store.dispatch('userMemberCards/add', { 
          userId: userId, 
          memberCardId: selectedMemberCardId.value 
        })
        toast.success('Member Card added')

        // Load User's Member Cards
        await store.dispatch('userMemberCards/ensureUserMemberCardListItems', { userId: userId });
        const loadedUserMemberCards = store.getters['userMemberCards/userMemberCardListItems'](userId)
        userMemberCards.value = JSON.parse(JSON.stringify(loadedUserMemberCards))
      } catch (err) {
        toast.error('Could not add MemberCard: ' + err)
      }

      loading.value = false
    }

    async function loadRequiredData() {
      loading.value = true

      // Load User
      await store.dispatch('users/ensureUser', { userId: userId });
      const loadedUser = store.getters['users/user'](userId)
      user.value = JSON.parse(JSON.stringify(loadedUser))

      // Load Member Cards
      await store.dispatch('memberCards/ensureMemberCardListItems');
      const loadedMemberCards = store.getters['memberCards/memberCardListItems']
      memberCards.value = loadedMemberCards

      // Load User's Member Cards
      await store.dispatch('userMemberCards/getUserMemberCardListItems', { userId: userId });
      const loadedUserMemberCards = store.getters['userMemberCards/userMemberCardListItems'](userId)
      userMemberCards.value = JSON.parse(JSON.stringify(loadedUserMemberCards))

      loading.value = false
    }

    loadRequiredData();

    return {
      loading,

      user,
      userMemberCards,
      memberCards,

      addMemberCardOpened,
      addMemberCardVisible,
      selectedMemberCardId,

      addMemberCard,
      confirmAddMemberCard
    }
  }
};
</script>
-->
